import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import AtsRecordView from '../../components/ats/AtsRecordView';
import ModelLab from '../../components/ats/ModelLab';
import NewResume from '../../components/ats/NewResume';
import type { AtsJob, JobDraft } from '../../components/ats/NewResume';
import { AtsStyles, Btn, C, ModelChip, ScoreRing, Spinner, VerdictPill, fmtWhen, modelName, rupees } from '../../components/ats/ui';
import { useAdminSession } from '../../context/TrackerContext';
import { atsDelete, atsGet, atsList, atsOptions } from '../../lib/api';
import type { AtsListItem, AtsModelInfo, AtsModels, AtsRecord } from '../../lib/api';

// ── ATS Resume AI (admin) ────────────────────────────────────────────────────
// ① Upload a job and a resume → ② the Match report (eligibility, skills,
// projects; education is never changed) → ③ the ATS resume, worded for the
// job, next to the AI Coach. Compare models writes one resume with several
// Claude models side by side; the Model Lab shows how each model does and
// sets the default for each step. Server: routes/atsResume.js. The app has
// its own screen (admin_ats_resume_screen.dart).

const POLL_MS = 2500;
type View = 'list' | 'new' | 'open' | 'lab';
type Filter = 'all' | 'ready' | 'choices' | 'working' | 'failed';

function stateOf(r: AtsListItem): { key: Filter; label: string; bg: string; color: string } {
  if (r.busy) return { key: 'working', label: 'AI working…', bg: '#ede9fe', color: '#5b21b6' };
  if (r.step === 'done') return { key: 'ready', label: 'Ready', bg: '#dcfce7', color: '#166534' };
  if (r.error && r.verdict === '') return { key: 'failed', label: 'Check failed', bg: '#fee2e2', color: '#991b1b' };
  return { key: 'choices', label: 'Needs your choices', bg: '#fef3c7', color: '#92400e' };
}

const FILTERS: [Filter, string][] = [['all', 'All'], ['ready', 'Ready'], ['choices', 'Needs choices'], ['working', 'Working'], ['failed', 'Failed']];

const Stat: React.FC<{ icon: string; label: string; value: React.ReactNode; tone: string }> = ({ icon, label, value, tone }) => (
  <div className="bg-white rounded-3xl border border-gray-100 ats-card p-4 sm:p-5 flex items-center gap-3.5">
    <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shrink-0" style={{ background: tone }}>{icon}</span>
    <div className="min-w-0">
      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider truncate">{label}</p>
      <p className="text-xl font-black text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const ResumeCard: React.FC<{ r: AtsListItem; onOpen: () => void; onDelete: () => void }> = ({ r, onOpen, onDelete }) => {
  const st = stateOf(r);
  return (
    <div onClick={onOpen} className="group relative bg-white rounded-3xl border border-gray-100 ats-card ats-lift p-5 cursor-pointer flex flex-col" data-resume={r._id}>
      <button onClick={e => { e.stopPropagation(); onDelete(); }} title="Delete" aria-label="Delete"
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">✕</button>
      <div className="flex items-start gap-4">
        {r.busy ? (
          <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center bg-purple-50 shrink-0"><Spinner size={26} /></div>
        ) : r.verdict ? (
          <ScoreRing value={r.step === 'done' ? r.scoreAfter : r.scoreBefore} size={68} stroke={7} muted={r.step !== 'done'} />
        ) : (
          <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center bg-red-50 text-2xl shrink-0">⚠️</div>
        )}
        <div className="flex-1 min-w-0 pr-5">
          <p className="font-black text-gray-900 truncate">{r.candidateName || r.fileName || 'New resume'}</p>
          <p className="text-sm text-gray-600 truncate">{r.jobTitle}{r.company ? ` · ${r.company}` : ''}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: st.bg, color: st.color }}>{st.label}</span>
            <VerdictPill verdict={r.verdict} />
          </div>
        </div>
      </div>
      {r.step === 'done' && r.scoreAfter > r.scoreBefore && (
        <p className="text-xs font-bold text-gray-500 mt-3">ATS {r.scoreBefore}% → <span className="text-green-700">{r.scoreAfter}%</span> <span className="text-green-700">(+{r.scoreAfter - r.scoreBefore})</span></p>
      )}
      <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-gray-50 text-[11px] text-gray-400" style={{ marginTop: r.step === 'done' ? undefined : 12 }}>
        <span className="truncate">{fmtWhen(r.createdAt)}{r.tailoredFrom ? ' · ↪ another job' : ''}</span>
        <span className="flex items-center gap-2 shrink-0"><ModelChip id={r.models.make} /> {rupees(r.costInr)}</span>
      </div>
    </div>
  );
};

const AtsResumeAI: React.FC = () => {
  const { adminPassword: pw } = useAdminSession();
  const [view, setView] = useState<View>('list');
  const [items, setItems] = useState<AtsListItem[]>([]);
  const [month, setMonth] = useState({ resumes: 0, inr: 0, avgGain: 0 });
  const [defaults, setDefaults] = useState<AtsModels | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<AtsJob[]>([]);
  const [models, setModels] = useState<AtsModelInfo[]>([]);
  const [rec, setRec] = useState<AtsRecord | null>(null);
  const [retryJob, setRetryJob] = useState<JobDraft | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const openId = useRef('');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await atsList(pw);
      setItems(res.items);
      setMonth(res.month);
      setDefaults(res.defaults);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [pw]);

  // Job Board jobs and the models (with their real costs)
  const loadOptions = useCallback(async () => {
    try {
      const res = await atsOptions(pw);
      setJobs(res.jobs);
      setModels(res.models);
      setDefaults(res.defaults);
    } catch (err: any) { toast.error(err.message); }
  }, [pw]);

  useEffect(() => { loadList(); loadOptions(); }, [loadList, loadOptions]);

  const open = useCallback(async (id: string) => {
    openId.current = id;
    setView('open');
    setRec(null);
    try {
      const res = await atsGet(pw, id);
      if (openId.current === id) setRec(res.resume);
    } catch (err: any) { toast.error(err.message); setView('list'); }
  }, [pw]);

  // Keep the open resume fresh while the AI works on it
  const refresh = useCallback(async (next?: AtsRecord) => {
    if (next) { setRec(next); return; }
    if (!openId.current) return;
    try {
      const res = await atsGet(pw, openId.current);
      if (res.resume._id === openId.current) setRec(res.resume);
    } catch { /* the next poll tries again */ }
  }, [pw]);

  const working = !!rec && (!!rec.busy || !!rec.compare?.runs.some(r => r.busy));
  useEffect(() => {
    if (view !== 'open' || !working) return;
    const t = setTimeout(() => refresh(), POLL_MS);
    return () => clearTimeout(t);
  }, [view, rec, working, refresh]);

  const startNew = (initial?: JobDraft) => {
    setRetryJob(initial);
    openId.current = '';
    setView('new');
    loadOptions();
  };
  const backToList = () => { openId.current = ''; setRec(null); setView('list'); loadList(); };
  const goLab = () => { openId.current = ''; setRec(null); setView('lab'); };

  const remove = async (r: AtsListItem) => {
    if (!window.confirm(`Delete the ATS resume for ${r.candidateName || r.fileName} (${r.jobTitle})?`)) return;
    try {
      await atsDelete(pw, r._id);
      setItems(list => list.filter(x => x._id !== r._id));
      toast.success('Deleted');
    } catch (err: any) { toast.error(err.message); }
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(r => (filter === 'all' || stateOf(r).key === filter)
      && (!q || [r.candidateName, r.fileName, r.jobTitle, r.company].some(x => (x || '').toLowerCase().includes(q))));
  }, [items, search, filter]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: items.length, ready: 0, choices: 0, working: 0, failed: 0 };
    for (const r of items) c[stateOf(r).key]++;
    return c;
  }, [items]);

  const tab = view === 'lab' ? 'lab' : 'resumes';
  return (
    <TrackerLayout>
      <AtsStyles />
      <div className="space-y-5 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl shadow-xl" style={{ background: `linear-gradient(135deg, ${C.purple} 0%, #3a2c5c 55%, ${C.purple2} 100%)` }}>
          <div className="absolute -right-10 -top-24 w-80 h-80 rounded-full ats-glow" style={{ background: 'radial-gradient(circle, rgba(240,180,41,.28), transparent 65%)' }} />
          <div className="absolute left-1/3 -bottom-24 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,101,53,.14), transparent 65%)' }} />
          <div className="relative px-5 sm:px-7 py-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-lg" style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.orange})` }}>✨</span>
                <div>
                  <h1 className="text-2xl font-black text-white tracking-tight">ATS Resume AI</h1>
                  <p className="hidden sm:block text-white/60 text-sm">Tailor any resume to any job in minutes — facts and education never change.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex bg-white/10 rounded-2xl p-1 backdrop-blur">
                {([['resumes', '📄 Resumes'], ['lab', '🧪 Model Lab']] as const).map(([k, label]) => (
                  <button key={k} onClick={() => (k === 'lab' ? goLab() : backToList())}
                    className={`px-4 py-2 rounded-xl text-xs font-extrabold transition-all ${tab === k ? 'bg-white text-[#2b2240] shadow' : 'text-white/70 hover:text-white'}`}>{label}</button>
                ))}
              </div>
              <Btn kind="gold" size="lg" onClick={() => startNew()}>＋ New resume</Btn>
            </div>
          </div>
        </div>

        {(view === 'new' || view === 'open') && (
          <button onClick={backToList} className="text-xs font-extrabold text-purple-700 hover:underline">← All resumes</button>
        )}

        {view === 'list' && (
          <div className="space-y-5 ats-fade">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Stat icon="📄" label="Made this month" value={month.resumes} tone="#f5f3ff" />
              <Stat icon="📈" label="Avg. score gain" value={month.resumes ? <span className="text-green-700">+{month.avgGain}%</span> : '—'} tone="#ecfdf5" />
              <Stat icon="💰" label="AI spend this month" value={rupees(month.inr)} tone="#fffbeb" />
              <Stat icon="🤖" label="AI model" value={defaults ? modelName(defaults.make) : '—'} tone="#eff6ff" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, job or company"
                  className="w-full pl-10 pr-4 py-2.5 rounded-2xl text-sm bg-white border border-gray-200 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-100" />
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                {FILTERS.map(([k, label]) => (
                  <button key={k} onClick={() => setFilter(k)}
                    className={`shrink-0 px-3.5 py-2 rounded-xl text-xs font-extrabold border transition-all ${filter === k ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}
                    style={filter === k ? { background: C.purple } : {}}>
                    {label}{counts[k] > 0 && <span className={`ml-1.5 ${filter === k ? 'text-white/70' : 'text-gray-400'}`}>{counts[k]}</span>}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Spinner size={32} /></div>
            ) : items.length === 0 ? (
              <div className="bg-white rounded-3xl border border-gray-100 ats-card p-12 sm:p-16 text-center">
                <div className="mx-auto w-20 h-20 rounded-3xl flex items-center justify-center text-4xl shadow-lg" style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.orange})` }}>📄</div>
                <p className="text-xl font-black text-gray-900 mt-5">No ATS resumes yet</p>
                <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">Upload a resume and a job. The AI checks the match, then rewrites only the wording so it passes ATS and gets more interview calls.</p>
                <Btn kind="gold" size="lg" className="mt-6" onClick={() => startNew()}>＋ Make the first one</Btn>
              </div>
            ) : shown.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-12">Nothing matches. Try another search or filter.</p>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {shown.map(r => <ResumeCard key={r._id} r={r} onOpen={() => open(r._id)} onDelete={() => remove(r)} />)}
              </div>
            )}
          </div>
        )}

        {view === 'new' && (
          <NewResume key={retryJob ? 'retry' : 'new'} pw={pw} jobs={jobs} model={defaults?.make ?? ''} initial={retryJob} onStarted={open} />
        )}

        {view === 'open' && (
          !rec ? (
            <div className="flex justify-center py-20"><Spinner size={32} /></div>
          ) : (
            <AtsRecordView rec={rec} pw={pw} jobs={jobs} models={models} onChanged={refresh} onOpen={open}
              onRetry={() => startNew({ jobId: rec.jobId, jobTitle: rec.jobTitle, company: rec.company, jd: rec.jd })} />
          )
        )}

        {view === 'lab' && (
          <ModelLab pw={pw} onOpen={open} />
        )}
      </div>
    </TrackerLayout>
  );
};

export default AtsResumeAI;
